import React from 'react';
import styled from 'styled-components';
import DropdownMenu from '../elements/DropdownMenu';
import { PSmall } from '../elements/Text';

// Icon components
const AddIcon = styled.i.attrs({ className: 'fa-solid fa-plus' })`
  color: ${({ theme }) => theme.colors.textRegular};
  font-size: 12px;
`;
const XIcon = styled.i.attrs({ className: 'fa-solid fa-xmark' })`
  color: ${({ theme }) => theme.colors.textRegular};
  font-size: 12px;
  cursor: pointer;
`;
const ChevronIcon = styled.i.attrs({ className: 'fa-solid fa-chevron-down' })`
  color: ${({ theme }) => theme.colors.textRegular};
  font-size: 12px;
  margin-left: 8px;
`;
const PillDivider = styled.span`
  display: inline-block;
  width: 2px;
  height: 16px;
  background: ${({ theme }) => theme.colors.bgActive};
  margin: 0 8px;
  vertical-align: middle;
`;

// Styled wrappers for pills
export const InactiveFilterPillWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: ${({ theme }) => theme.colors.transparentBg};
  border-radius: 999px;
  padding: 4px 16px;
  border: 1px dashed ${({ theme }) => theme.colors.bgActive};
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  &:hover {
    background-color: ${({ theme }) => theme.colors.bgActive};
  }
`;
export const ActiveFilterPillWrapper = styled(InactiveFilterPillWrapper)`
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
  background-color: ${({ theme }) => theme.colors.bgActive};
`;
export const FilterLabel = styled(PSmall)`
  margin: 0;
  padding: 0;
  color: ${({ theme }) => theme.colors.textRegular};
  font-weight: regular;
  white-space: nowrap;
`;

// Dropdown item row
export const DropdownItemRow = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 16px;
  cursor: pointer;
  gap: 8px;
`;
export const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  width: 100%;
  cursor: pointer;
  margin-bottom: 0;
`;
export const CheckboxIconWrapper = styled.span`
  display: flex;
  align-items: center;
`;
export const DropdownItemLabel = styled(PSmall)`
  margin: 0;
  margin-left: 8px;
  padding: 0;
  color: ${({ theme }) => theme.colors.textRegular};
  font-weight: regular;
  white-space: nowrap;
`;

// Generic pill filter component
export type PillFilterProps = {
  label: string;
  values: string[];
  placeholder?: string;
  onClear?: (e: React.MouseEvent) => void;
  onClick?: () => void;
  hideClear?: boolean;
  inactive?: boolean;
  children: React.ReactNode;
};

export const PillFilter: React.FC<PillFilterProps> = ({
  label,
  values,
  placeholder,
  onClear,
  onClick,
  hideClear,
  inactive,
  children,
}) => {
  let displayText = '';
  if (values.length > 2) {
    displayText = `${values.slice(0, 2).join(', ')} …`;
  } else if (values.length > 0) {
    displayText = values.join(', ');
  } else if (placeholder) {
    displayText = placeholder;
  }

  const pill = inactive ? (
    <InactiveFilterPillWrapper onClick={onClick}>
      <AddIcon />
      <FilterLabel>{label}</FilterLabel>
    </InactiveFilterPillWrapper>
  ) : (
    <ActiveFilterPillWrapper onClick={onClick}>
      {!hideClear && (
        <XIcon
          onClick={(e) => {
            e.stopPropagation();
            onClear && onClear(e);
          }}
        />
      )}
      <FilterLabel>
        {label}
        {displayText && (
          <>
            <PillDivider />
            <span style={{ color: '#888' }}>{displayText}</span>
          </>
        )}
      </FilterLabel>
      <ChevronIcon />
    </ActiveFilterPillWrapper>
  );

  return <DropdownMenu trigger={pill}>{children}</DropdownMenu>;
};

export default PillFilter;
